import { Box, styled } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { SolidButton } from "@/components/StoreComponents/StoreButton";

import {
  ResponsiveDialog,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";

enum PopupStatus {
  VERIFICATION = "verification",
  LEAVING = "leaving",
}

const AccountInputFormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  alignSelf: "center",
  padding: "24px",
  height: "100%",
  maxWidth: 380,
  width: "100%",
  ".resend": {
    color: theme.vars.palette.text.primary,
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: "24px",
  },

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {},
  ".input-container": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    width: "100%",
    marginTop: "24px",
    marginBottom: "24px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: "column",
      gap: "15px",
      marginTop: "24px",
      marginBottom: "24px",
    },
  },
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.vars.palette.text.primary,
}));

export const PaymentVerificationPopup = ({
  open: showEdit,
  onClose: closeEdit,
  onSave: onSaveVerification,
  displayMessage,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (code: string) => void;
  paymentMethod?: {
    displayName?: string;
    lastFour?: string;
  };
  displayMessage: string;
}) => {
  const [status, setStatus] = useState<PopupStatus>(PopupStatus.VERIFICATION);
  const [verificationCode, setVerificationCode] = useState<string>("");

  const onCloseEdit = () => {
    setStatus(PopupStatus.VERIFICATION);
    setVerificationCode(""); // Reset to empty value
    closeEdit();
  };

  const handleSubmit = () => {
    closeEdit();
    onSaveVerification(verificationCode);
  };

  return (
    <ResponsiveDialog open={showEdit} onClose={onCloseEdit}>
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            minWidth: 580,
            maxWidth: 580,
            width: "100%",
          },
          overflowY: "auto",
          maxHeight: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="payment-verification-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              padding: "24px 24px",
              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            <AccountInputFormContainer
              sx={{
                padding: "24px",
              }}
              className="payment-verification-container"
            >
              {/* Title */}
              <Box
                sx={(theme) => ({
                  fontWeight: 700,
                  fontSize: 22,
                  marginBottom: "12px",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: 18,
                    marginBottom: "8px",
                  },
                  textAlign: "center",
                })}
              >
                Verify your payment method
              </Box>

              {/* Description */}
              <Box sx={{ textAlign: "center", marginBottom: "24px" }}>
                {displayMessage}
              </Box>

              {/* Input Field */}
              <div className="input-container">
                <CheckoutInput
                  sx={{
                    width: "100%",
                    maxWidth: 380,
                  }}
                  label="CVV code"
                  onChange={(e) => setVerificationCode(e.target.value)}
                  value={verificationCode || ""}
                  placeholder="Enter CVV code"
                />
              </div>

              {/* Submit Button */}
              <SolidButton
                sx={{
                  width: "100%",
                  height: "50px",
                  backgroundColor: "#000",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "16px",
                  "&:hover": {
                    backgroundColor: "#333",
                  },
                }}
                onClick={handleSubmit}
                disabled={!verificationCode || verificationCode.length < 3}
              >
                Continue
              </SolidButton>
            </AccountInputFormContainer>
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};
